import { ajax } from "$lib/ajax";
import { API_BASE_URL, updateUrl, updateUrlReport } from "$lib/config/index.js";
import { csrfStore } from "$lib/csrfStore";
import type { Slot } from "$lib/stores/calendarStore";

import { navbarStore } from "./stores/navbarStore";

export async function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

let _csrf: string;
csrfStore.subscribe((value) => {
    _csrf = value;
});

export async function fetchPOST<T, D extends Record<string, unknown>>(
    url: string,
    data: D
): Promise<T> {
    const response = await ajax(url, JSON.stringify(data), "json", "POST");
    return response as T;
}

export type CalendarStoreSlots = {
    list: Slot[];
    selected: number;
};

export const getCalendar = async (): Promise<CalendarStoreSlots> => {
    const response = await ajax(`${API_BASE_URL}/transcription/calendar/slots`);

    return response;
};

const MAX_RETRIES = 5;
const RETRY_INTERVAL = 1000;
const ERR_MESSAGE =
    "We could not load data from our server. Please check if the internet connection is working properly.";

export type Errors = Record<string, string>;

export type GetRecordingResponse = {
    id: number;
    generating: number | null; // Assuming _get_elapsed_percent returns a number
    templates: LetterStructureTemplate[];
    letter_structure_template_id: number | null;
    macros: Macro[];
    title: string;
    datetime: string;
    duration: number;
    doctor_id: number;
    audio_url: string | null;
    accepted_patients: Person[];
    accepted_recipients: Person[];
    suggested_patients: Person[];
    suggested_recipients: Person[];
};

export type Recording = {
    patientSelected: boolean;
    selected_patient_id: number | null;
    time: string;
    recording: GetRecordingResponse;
};

export type Person = {
    id: number;
    name: string;
    email: string;
    origin: string;
    type: "patient" | "recipient";
    imeddoc_id?: number;
    address1?: string;
    address2?: string;
    county?: string;
    birth?: string;
    contact_type?: string;
    is_recipient?: boolean;
    copy_to?: boolean;
};

export type Macro = {
    // Define the structure of a macro
    // For example:
    id: number;
    name: string;
    text: string;
};

export async function getRecording(
    errors: Errors,
    save: boolean,
    maxRetries: number = MAX_RETRIES,
    retryInterval: number = RETRY_INTERVAL,
    errMessage: string = ERR_MESSAGE
): Promise<GetRecordingResponse> {
    return new Promise((resolve, reject) => {
        let retries = 0;

        const attempt = async () => {
            try {
                const url = `${API_BASE_URL}/transcription/dictation/blank/${save ? "1" : "0"}/`;
                const response = await ajax(url, {});
                delete errors.getRecording;
                resolve(response as GetRecordingResponse);
                return;
            } catch (e) {
                errors.getRecording = errMessage;
                retries++;
                console.log("error");
                console.log(errors);

                if (retries < maxRetries) {
                    setTimeout(attempt, retryInterval);
                } else {
                    reject(errors);
                }
            }
        };

        attempt();
    });
}

export type ScannedDocument = {
    id: number;
    document_date: string;
    description: string;
    file_extension: string;
    uploaded_at: string;
    imeddoc_document_id: string | null;
    imeddoc_document_enc_id: string | null;
    uploaded_by__first_name: string;
    uploaded_by__last_name: string;
};

export type CustomStatus = {
    label: string;
    value: string;
    color: string;
};

export type Dictation = {
    id: number;
    audio_url: string;
    pdf_url: string | null;
    docx_url: string | null;
    doctor_id: number;
    letter: string;
    transcript: string;
    raw_transcript: string;
    datetime: string;
    document_title: string;
    position_text: string;
    current_position: number;
    dictation_count: number;
    people: {
        accepted_patients: Patient[];
        accepted_recipients: Recipient[];
        suggested_patients: Patient[];
        suggested_recipients: Recipient[];
    };
    is_deleted: boolean;
    is_uploaded: boolean;
    is_done: boolean;
    is_verified: boolean;
    verified_by: string;
    verified_by_me: boolean;
    letter_template_id: number | null;
    letter_structure_template_id: number | null;
    signature_id: number | null;
    sent_emails: SentEmail[];
    generating: number | null;
    step: number;
    is_review_finished: boolean;
    origin_created_by: string;
    origin_created_by_id: number | null;
    pms_uploads: PMSUpload[];
    shared: SharedLetter[];
    copy_to_patient: boolean;
    share_uuid: string;
    original_letter: string;
    word_count: number;
    scanned_documents: ScannedDocument[];
    custom_status: {
        id: number;
        value: string;
        label: string;
        color: string;
        order: number;
    } | null;
};

export type Patient = {
    id: number;
    name: string;
    email: string;
    origin: string;
    imeddoc_id: string;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    county: string;
    type: string;
    birth: string;
    is_recipient: boolean;
    copy_to: boolean;
    enc_id?: string;
};

export type Recipient = {
    id: number;
    name: string;
    email: string;
    origin: string;
    address1: string;
    address2: string;
    county: string;
    type: string;
    contact_type: string;
    is_recipient: boolean;
    copy_to: boolean;
};

type SentEmail = {
    id: number;
    datetime: string;
    to: string;
    from: string;
    by: string;
};

type PMSUpload = {
    id: number;
    datetime: string;
    document_title: string;
};

type SharedLetter = {
    id: number;
    shared_with: string;
    opened: boolean;
    opened_at: string;
};

export type Doctor = {
    id: number;
    name: string;
    metadata: string;
};

export type LetterTemplate = {
    id: number;
    name: string;
    default: boolean;
};

export type Filter = {
    id: number;
    name: string;
};

type Dictater = {
    id: number;
    first_name: string;
    last_name: string;
};

export type DictationResponse = {
    dictation: Dictation;
    doctors: Doctor[];
    letter_templates: LetterTemplate[];
    filters: Filter[];
    has_connected_email: boolean;
    has_connected_pms: boolean;
    step: number;
    signatures: Signature[];
    templates: LetterTemplate[];
    filters_pills: Pill[];
    more_pills: Pill[];
    dictaters: Dictater[];
    letter_structure_templates: LetterStructureTemplate[];
};

export type LetterStructureTemplate = {
    id: number;
    name: string;
    is_default: boolean;
};

type Pill = {
    id: number;
    name: string;
};

export type Signature = {
    id: number;
    name: string;
};

export type GetDictationData = {
    id: number | null;
};

export async function getDictation(data?: GetDictationData): Promise<DictationResponse> {
    if (data === null || data === undefined) {
        data = { id: null };
    }
    const response = await ajax(`${API_BASE_URL}/transcription/get/dictation`, {
        id: data.id,
    });
    console.log(response);
    return response as DictationResponse;
}

export async function updateDictation(data: Partial<Dictation>, id: number): Promise<any> {
    const context = { ...data, id: id };
    return await ajax(updateUrl, context);
}

export async function updateDictationAttributes(
    data: Partial<Dictation>,
    id: number | number[]
): Promise<any> {
    const context = {
        attributes: JSON.stringify(data),
        id: JSON.stringify(id),
    };
    return await ajax(updateUrl, context);
}

export async function updateReportAttributes(data: Partial<Dictation>, id: number): Promise<any> {
    const context = {
        attributes: JSON.stringify(data),
        id: id,
    };
    return await ajax(updateUrlReport, context);
}

export function getDefault<T extends { id: number; default?: boolean }>(
    items: T[] | null | undefined,
    id: number | null | undefined,
    error_message?: string
): T | string {
    if (items === null || items === undefined) {
        return error_message || "No items found";
    }

    if (items.length === 0) {
        return error_message || "No items found";
    }

    if (id !== null && id !== undefined) {
        const item = items.find((item) => item.id === id);
        if (item) {
            return item;
        }
    }

    const default_item = items.find((item) => item.default === true);

    if (default_item) {
        return default_item;
    }

    return items[0];
}

export async function generatePDF(dictation: Dictation, errors: string[]): Promise<void> {
    const url = `${API_BASE_URL}/transcription/letter/pdf`;
    const data = {
        id: dictation.id,
    };
    const response = await ajax(url, data);

    if (!response.error) {
        dictation.pdf_url = response.url;
    } else {
        errors.push(response.error);
    }
}

type GetNextResponse = {
    // Define the structure of the response
    // This is a placeholder and should be replaced with the actual structure
    id: number;
    // ... other properties
};

export async function getNext(id: number, direction: string): Promise<GetNextResponse> {
    const url = `${API_BASE_URL}/transcription/get/next/`;
    const data = { id: id, direction: direction };

    const response = await ajax(url, data);

    return response as GetNextResponse;
}

export async function updatePerson(
    personId: number,
    attributes: { is_recipient: boolean; copy_to?: boolean }
) {
    const url = `${API_BASE_URL}/transcription/people/update/individual`;

    const data = {
        person_id: personId,
        attributes: JSON.stringify(attributes),
    };

    const response = await ajax(url, data);

    if (!response.success) {
        console.error("Failed to update person");
        console.error(response.error);
        return false;
    }
    return true;
}

type GetFilteredDictationsRequest = {
    filters: Record<string, any>;
    page: number;
    per_page: number;
    sort_by?: string;
    sort_order?: "asc" | "desc";
    dictation_id?: number | null;
};

export type GetFilteredDictationsResponse = {
    dictations: Dictation[];
    total_count: number;
    total_pages: number;
    current_page: number;
    current_item_index: number;
    per_page: number;
    todo_count: number;
    verified_count: number;
    done_count: number;
    doctors: Doctor[];
    letter_templates: LetterTemplate[];
    has_connected_email: boolean;
    has_connected_pms: boolean;
    signatures: Signature[];
    templates: LetterTemplate[];
    dictaters: Dictater[];
    letter_structure_templates: LetterStructureTemplate[];
    custom_statuses: CustomStatus[];
};

type FilteredDictationsInput = {
    filters: Record<string, any>;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: "asc" | "desc";
    id?: number | null;
};

export async function getFilteredDictations(
    filteredDictationsInput: FilteredDictationsInput,
    isPolling = false,
    signal?: AbortSignal
): Promise<GetFilteredDictationsResponse> {
    console.log(`[getFilteredDictations] Starting with params:`, {
        isPolling,
        filters: filteredDictationsInput.filters,
        page: filteredDictationsInput.page,
        per_page: filteredDictationsInput.per_page,
        id: filteredDictationsInput.id,
    });

    const params: any = {
        filters: JSON.stringify(filteredDictationsInput.filters),
    };

    // Only include parameters if they are defined
    if (filteredDictationsInput.page !== undefined) {
        params.page = filteredDictationsInput.page;
    }

    if (filteredDictationsInput.per_page !== undefined) {
        params.per_page = filteredDictationsInput.per_page;
    }

    if (filteredDictationsInput.sort_by) {
        params.sort_by = filteredDictationsInput.sort_by;
    }

    if (filteredDictationsInput.sort_order) {
        params.sort_order = filteredDictationsInput.sort_order;
    }

    if (filteredDictationsInput.id !== undefined && filteredDictationsInput.id !== null) {
        params.id = filteredDictationsInput.id;
    }

    try {
        const requestId = Date.now();
        console.log(
            `[getFilteredDictations:${requestId}] Sending request to ${
                isPolling ? "polling" : "regular"
            } endpoint`
        );

        const response = await ajax(
            isPolling
                ? `${API_BASE_URL}/transcription/dictations/filter/polling`
                : `${API_BASE_URL}/transcription/dictations/filter`,
            params,
            "json",
            "POST",
            signal
        );

        if (!response || response instanceof Blob) {
            console.error(`[getFilteredDictations:${requestId}] Invalid response format`, response);
            throw new Error("Failed to fetch filtered dictations");
        }

        // Cast the response to the expected type
        const typedResponse = response as GetFilteredDictationsResponse;

        console.log(
            `[getFilteredDictations:${requestId}] Response received with ${typedResponse.dictations.length} dictations`
        );

        // No longer update navbar counts here - that's now handled by syncNavbarCounts only

        return typedResponse;
    } catch (error) {
        // Check if this is an abort error (could come from various sources)
        if (
            (error instanceof DOMException && error.name === "AbortError") ||
            (error instanceof Error && error.name === "AbortError") ||
            (error && typeof error === "object" && "name" in error && error.name === "AbortError")
        ) {
            console.warn("Fetch request was aborted");
            // Simply re-throw the error since it's already properly formatted
            throw error;
        }

        console.error("Failed to fetch filtered dictations:", error);
        throw new Error("Failed to fetch filtered dictations");
    }
}

export async function loadPdf(id: number): Promise<{ url: string; warnings: string[] }> {
    const response = await ajax(`${API_BASE_URL}/transcription/letter/pdf`, {
        id: id,
    });
    return response;
}

type ClearLetterResponse = {
    success: boolean;
    error?: string;
};

export async function clearLetter(dictationId: number): Promise<ClearLetterResponse> {
    const url = `${API_BASE_URL}/transcription/clear/letter`;
    const response = await ajax(url, { dictation_id: dictationId });
    return response;
}

export async function updatePeople(
    updates: Array<{ person_id: number; attributes: Record<string, unknown> }>
) {
    try {
        const url = `${API_BASE_URL}/transcription/people/update`;
        const response = await ajax(url, {
            batch_updates: JSON.stringify(updates),
        });
        return response.success;
    } catch (error) {
        console.error("Error updating people:", error);
        return false;
    }
}
